import { CompServe } from "@/service"
import { useAppStore } from "@/store"
import { isZWEnv, ZWApis } from "@/utils"
import { downloadFile, previewImgList } from "@/utils/basic"
import { closeToast, Icon, Image, Loading, showFailToast } from "vant"
import { computed, defineComponent, onMounted, ref, shallowRef } from "vue"
import classes from "./index.module.less"
import { usePreviewVideo } from "./previewVideo"

// 合并外部 class 与内置 class
const useClass = (defaultClass, propClass) => {
  if (Array.isArray(propClass)) {
    return [defaultClass, ...propClass]
  } else if (typeof propClass === "string") {
    return [defaultClass, propClass]
  } else if (typeof propClass === "object") {
    return [defaultClass, propClass]
  } else {
    return defaultClass
  }
}

export const FileItem = defineComponent({
  name: "MobileFileItem",
  props: {
    config: { type: Object },
    modelValue: { type: [String, File] },
    disabled: { type: Boolean },
    readonly: { type: Boolean },
    handlePath: { type: Function }, // 图片地址处理
    clickFile: { type: Function },
    itemClass: { type: [Array, String, Object] },
    // 上传成功后自动读取文件
    // Files 中设为 false，用于处理以 modelValue 为 key 导致重复渲染的问题
    readAfterUpload: { default: true, type: Boolean },
    useScale: { type: Boolean, default: false } //是否使用缩略图下载接口
  },
  emits: ["update:modelValue", "update:config", "error"],
  setup(props, { emit }) {
    const appStore = useAppStore()

    const itemClass = computed(() => {
      const propClass = props.itemClass
      const defaultClass = classes.fileItem
      return useClass(propClass, defaultClass)
    })

    const file = computed({
      get() {
        return props.modelValue
      },
      set(v) {
        emit("update:modelValue", v)
      }
    })

    const isLoading = ref(false)

    const clickFile = () => {
      if (!props.readonly) {
        const { isImage, isVideo, path } = fileConfig.value
        if (props.clickFile) props.clickFile(fileConfig.value)
        else if (isImage) {
          previewImgList([path])
        } else if (isVideo) {
          usePreviewVideo({ paths: [{ path }] })
        } else downloadFile(fileConfig.value)
      }
    }

    const fileConfig = shallowRef({})
    const updateConfig = (config) => {
      fileConfig.value = config
      emit("update:config", fileConfig.value)
    }

    // 通过 file id 读取 base64
    const readPath = (uuid) => {
      const config = appStore.getCacheFile(uuid)
      if (config) updateConfig(config)
      else {
        isLoading.value = true
        let api = CompServe.downloadFile
        if (props.useScale) {
          api = CompServe.downloadScaleFile
        }
        api(uuid)
          .then((res) => {
            if (res.success) {
              const config = appStore.cacheFile(res.data)
              updateConfig(config)
            }
          })
          .finally(() => {
            isLoading.value = false
          })
      }
    }
    // 传入的是一个文件对象，需要执行上传
    const uploadFile = () => {
      isLoading.value = true
      CompServe.uploadFile(file.value)
        .then((res) => {
          if (res.success) {
            emit("update:modelValue", res.data)
            // 缓存数据
            if (props.readAfterUpload) readPath(res.data)
          } else {
            emit("error", file.value)
          }
          isLoading.value = false
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    // 初始化代理：判断是上传还是读取
    const initProxy = () => {
      if (file.value) {
        if (typeof file.value === "string") readPath(file.value)
        else uploadFile()
      }
    }

    onMounted(initProxy)

    return () => {
      // 加载中
      if (isLoading.value) {
        return (
          <div class={itemClass.value}>
            <div class={classes.loading}>
              <Loading />
            </div>
          </div>
        )
      }
      // 图片附件
      else if (fileConfig.value.isImage) {
        return (
          <div class={itemClass.value}>
            <img
              class={classes.image}
              src={fileConfig.value.path}
              onClick={clickFile}
            />
          </div>
        )
      }
      // 视频附件
      else if (fileConfig.value.isVideo) {
        return (
          <div class={itemClass.value} onClick={clickFile}>
            <div class={classes.videoPane}>
              <video class={classes.videoPane}>
                <source src={fileConfig.value.path} />
              </video>
              <img src={require("@/assets/merchant/play.png")} alt="" />
            </div>
          </div>
        )
      }
      // 其他类型附件
      else {
        return (
          <div class={itemClass.value} onClick={clickFile}>
            <div class={classes.unImgFilePane}>
              <Icon class={classes.unImage} name="description" />
              <div class={classes.unImgFileTitle}>{fileConfig.value.name}</div>
            </div>
          </div>
        )
      }
    }
  }
})

export const Files = defineComponent({
  name: "MobileFiles",
  inheritAttrs: true,
  props: {
    modelValue: { type: Array, default: () => [] },
    disabled: { type: Boolean },
    handlePath: { type: Function }, // 图片地址处理
    boxClass: { type: [Array, String, Object] },
    itemClass: { type: [Array, String, Object] },
    paneClass: { type: [Array, String, Object] },
    limitSize: { type: Number, default: 10 }, // 附件大小限制，单位 M
    // 附件类型限制
    accept: { type: String },
    type: { type: String, default: "file" }, // 文件类型，file 或 image
    placeholder: { type: String },
    maxCount: { type: Number } // 附件数量限制，不传默认不限制
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const insideValue = computed({
      get() {
        return props.modelValue
      },
      set(newValue) {
        emit("update:modelValue", newValue)
      }
    })

    const boxClass = computed(() => {
      const propClass = props.boxClass
      const defaultClass = classes.listBox
      return useClass(propClass, defaultClass)
    })
    const paneClass = computed(() => {
      const propClass = props.paneClass
      const defaultClass = classes.uploader
      return useClass(propClass, defaultClass)
    })

    // 文件限制判断
    const fileLimit = (file) => {
      if (!file) return false
      else if (props.limitSize > 0) {
        const { size } = file
        const isExceed = size > props.limitSize * 1024 * 1024
        if (isExceed) showFailToast(`文件不能超过 ${props.limitSize} M`)
        return !isExceed
      }
      return true
    }

    // 选中附件
    const changeFile = (e) => {
      const file = e.target.files[0]
      if (fileLimit(file)) {
        insideValue.value = [...insideValue.value, file]
        inputRef.value.value = ""
      }
    }
    // 删除
    const useRemove = (index) => {
      return () => {
        insideValue.value.splice(index, 1)
        insideValue.value = [...insideValue.value]
      }
    }
    const deleteRenderer = (i) => {
      if (!props.disabled) {
        return (
          <Icon name="clear" class={classes.clear} onClick={useRemove(i)} />
        )
      }
    }
    // 点击附件
    const clickFile = (config) => {
      if (config) {
        const { isImage, isVideo, uuid, path } = config
        if (isImage) {
          // 返回 图片数据，以及被点击图片所在序号
          const { paths, index } = configs.value.reduce(
            ({ paths, index }, item) => {
              if (item.isImage) {
                return {
                  paths: [...paths, item.path],
                  index: item.uuid === uuid ? paths.length : index
                }
              }
              return { paths, index }
            },
            { paths: [], index: 0 }
          )
          // 从 configs 中获取 图片列表和当前图片序号
          previewImgList(paths, index)
        } else if (isVideo) {
          usePreviewVideo({ paths: [{ path }] })
        } else downloadFile(config)
      }
    }
    // 上传操作代理
    const uploadProxy = () => {
      if (isZWEnv()) {
        ZWApis({
          action: "chooseFile",
          data: {
            count: 1,
            materialSize: props.limitSize,
            sourceType:
              props.type === "file"
                ? ["camera", "file", "album"]
                : ["camera", "album"],
            type: accept.value
          }
        })
          .then((result) => {
            if (result.status === "success") {
              const [file_id] = result.filePath
              insideValue.value = [...insideValue.value, file_id]
            }
            closeToast()
          })
          .catch(() => {
            closeToast()
          })
      } else {
        inputRef.value.click()
      }
    }

    const inputRef = ref()

    // 附件列表
    const configs = ref([])
    const FileItemRenderer = () => {
      return insideValue.value.map((item, i) => (
        <div class={boxClass.value} key={item}>
          <FileItem
            vModel={insideValue.value[i]}
            vModel:config={configs.value[i]}
            clickFile={clickFile}
            itemClass={props.itemClass}
            readAfterUpload={false}
            onError={() => {
              insideValue.value.splice(i, 1)
              configs.value.splice(i, 1)
            }}
          />
          {deleteRenderer(i)}
        </div>
      ))
    }
    // 上传按钮
    const UploadBtnRenderer = () => {
      if (
        !props.disabled &&
        (!props.maxCount || insideValue.value.length < props.maxCount)
      ) {
        return (
          <label
            class={[boxClass.value, classes.uploadBox]}
            onClick={uploadProxy}
          >
            <Icon name="add-o" class={classes.icon} />
          </label>
        )
      }
    }

    const accept = computed(() => {
      if (props.accept?.length) {
        return props.accept
      } else if (props.type === "image") {
        return ".png,.jpg,.jpeg,.heic"
      } else if (props.type === "file") {
        return ".doc,.docx,.et,.xls,.xlsx,.wps,.pdf,.png,.jpg,.jpeg,.heic"
      }
      return ".doc,.docx,.et,.xls,.xlsx,.wps,.pdf,.png,.jpg,.jpeg,.heic"
    })

    return () => (
      <div>
        {props.placeholder && (
          <div class={classes.placeholder}>{props.placeholder}</div>
        )}
        <div class={paneClass.value}>
          {FileItemRenderer()}
          {UploadBtnRenderer()}
          <input
            ref={inputRef}
            type="file"
            onChange={changeFile}
            class={classes.input}
            accept={accept.value}
          />
        </div>
      </div>
    )
  }
})
